// Shared application theme state, synced with Bootstrap's `data-bs-theme` on
// document.documentElement and persisted in localStorage as `cc-theme`.

/** @typedef {'light' | 'dark' | 'auto'} ThemeMode */
/** @typedef {'light' | 'dark'} EffectiveTheme */

const STORAGE_KEY = "cc-theme";

export const themeState = $state({
    mode: /** @type {ThemeMode} */ ("dark"),
    effective: /** @type {EffectiveTheme} */ ("dark")
});

/**
 * @returns {string | null}
 */
export function getStoredTheme() {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(STORAGE_KEY);
}

/**
 * @returns {ThemeMode}
 */
export function getPreferredTheme() {
    const stored = getStoredTheme();
    if (stored === "light" || stored === "dark" || stored === "auto") {
        return stored;
    }
    return "dark";
}

/**
 * Resolve user mode to the palette key used by Konva timeline rendering.
 *
 * @param {ThemeMode} mode
 * @returns {EffectiveTheme}
 */
export function resolveEffectiveTheme(mode) {
    if (mode === "light") return "light";
    if (mode === "dark") return "dark";
    if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }
    return "light";
}

/**
 * Bootstrap attribute value for `data-bs-theme`. Auto resolves to light or dark.
 *
 * @param {ThemeMode} mode
 * @returns {EffectiveTheme}
 */
function bootstrapThemeAttribute(mode) {
    if (mode === "auto") {
        return resolveEffectiveTheme("auto");
    }
    return mode === "light" ? "light" : "dark";
}

/**
 * Apply theme: updates reactive store, DOM attribute, and localStorage.
 *
 * @param {ThemeMode} mode
 */
export function setAppTheme(mode) {
    const effective = resolveEffectiveTheme(mode);
    themeState.mode = mode;
    themeState.effective = effective;

    if (typeof document !== "undefined") {
        document.documentElement.setAttribute("data-bs-theme", bootstrapThemeAttribute(mode));
    }
    if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, mode);
    }
}

/**
 * Initialize theme from storage / OS preference. Safe to call multiple times.
 */
export function initAppTheme() {
    setAppTheme(getPreferredTheme());
}

/**
 * Re-apply theme when OS preference changes while mode is `auto`.
 */
export function handleSystemThemeChange() {
    const stored = getStoredTheme();
    if (stored !== "light" && stored !== "dark") {
        setAppTheme(getPreferredTheme());
    }
}
